import { Button } from "@/components/ui/button";
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/ui/phone-input";
import { config } from "@/config";
import { useRegister } from "@/services/context/register";
import {
    createUserMutation,
    createUserWithGoogleMutation,
} from "@/services/queries";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { CustomLink } from "..";

const generarVariacionesDominio = (dominio: string): string[] => {
    const base = dominio.replace(".com", "");
    const letras = base.split("");
    const variaciones = new Set<string>();

    // Errores comunes de TLD
    variaciones.add(`@${base}.con`);
    variaciones.add(`@${base}.co`);
    variaciones.add(`@${base}.vom`);
    variaciones.add(`@${base}c.om`);

    // Transposición de letras
    for (let i = 0; i < letras.length - 1; i++) {
        const transpuesto = [...letras];
        [transpuesto[i], transpuesto[i + 1]] = [
            transpuesto[i + 1],
            transpuesto[i],
        ];
        variaciones.add(`@${transpuesto.join("")}.com`);
    }

    // Letras faltantes
    for (let i = 0; i < letras.length; i++) {
        const omitido = [...letras];
        omitido.splice(i, 1);
        variaciones.add(`@${omitido.join("")}.com`);
    }

    // Letras duplicadas
    for (let i = 0; i < letras.length; i++) {
        const duplicado = [...letras];
        duplicado.splice(i, 0, letras[i]);
        variaciones.add(`@${duplicado.join("")}.com`);
    }

    // Elimina la variante exacta "@dominio" si está presente
    if (variaciones.has(`@${dominio}`)) {
        variaciones.delete(`@${dominio}`);
    }

    return Array.from(variaciones);
};

const incorrectDomains = {
    gmail: generarVariacionesDominio("gmail.com"),
    hotmail: generarVariacionesDominio("hotmail.com"),
    outlook: generarVariacionesDominio("outlook.com"),
    yahoo: generarVariacionesDominio("yahoo.com"),
};

const userDataSchema = z
    .object({
        display_name: z
            .string({
                required_error: "⚠️ Necesitamos saber como referirnos a ti.",
            })
            .min(2)
            .max(100),
        phone_number: z
            .string({
                required_error:
                    "⚠️ Necesitamos tu número de teléfono para comunicarnos contigo en caso de ser necesario.",
            })
            .regex(/^\+(\s*\d){11}$/, "El número de telefono no es válido")
            .min(1, "El número de teléfono es requerido"),
        email: z
            .string({
                required_error: "⚠️ El email es necesario para crear tu cuenta.",
            })
            .email("No parece ser un email válido.")
            .regex(/^[^@]+@[^@]+\.(com|net|org|edu|cl)$/, {
                message:
                    "Solo se permiten correos con dominios .com, .cl, .net, .org, o .edu",
            })
            .superRefine((email, ctx) => {
                const emailLowerCase = email.toLowerCase();
                for (const [provider, dominios] of Object.entries(
                    incorrectDomains,
                )) {
                    if (
                        dominios.some((dominio) =>
                            emailLowerCase.endsWith(dominio),
                        )
                    ) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: `¿Quisiste escribir @${provider}.com?`,
                        });
                        return false;
                    }
                }
                return true;
            }),
        password: z
            .string({
                required_error:
                    "⚠️ La contraseña te permite entrar a tu cuenta.",
            })
            .min(6, "La contraseña debe tener mínimo 6 caracteres."),
        confirm_password: z
            .string({
                required_error:
                    "⚠️ Te pedimos confirmar tu contraseña para disminuir errores.",
            })
            .min(6, "La contraseña debe tener mínimo 6 caracteres."),
    })
    .refine((data) => data.password === data.confirm_password, {
        path: ["confirm_password"],
        message: "Las contraseñas deben coincidir",
    });

type UserDataSchema = z.infer<typeof userDataSchema>;

const UserDataForm = () => {
    const { registryData, setRegistryData } = useRegister();
    const createUser = createUserMutation();
    const createUserWithGoogle = createUserWithGoogleMutation();

    const navigate = useNavigate();

    if (registryData === null) {
        navigate({ to: "/register/subscriptions" });
        return null;
    }

    if (createUser.isSuccess || createUserWithGoogle.isSuccess) {
        navigate({ to: "/register/confirmation" });
    }

    const form = useForm<UserDataSchema>({
        resolver: zodResolver(userDataSchema),
        defaultValues: registryData,
    });

    const handleSubmit = (values: UserDataSchema) => {
        const { email, password } = values;
        createUser.mutate({
            email,
            password,
            registryData: {
                ...registryData,
                display_name: values.display_name,
                phone_number: values.phone_number,
                email,
            },
        });
    };

    const handleGoogleSubmit = () => {
        const values = form.getValues();
        form.clearErrors(["display_name", "phone_number"]);

        if (!values.display_name || values.display_name.length < 2) {
            form.setError("display_name", {
                message: "Ingresa tu nombre antes de continuar con Google.",
            });
            return;
        }

        if (!/^\+(\s*\d){11}$/.test(values.phone_number || "")) {
            form.setError("phone_number", {
                message:
                    "Ingresa un teléfono válido antes de continuar con Google.",
            });
            return;
        }

        createUserWithGoogle.mutate({
            registryData: {
                ...registryData,
                display_name: values.display_name,
                phone_number: values.phone_number,
                email: values.email,
            },
        });
    };

    const renderError = (error: string) => {
        if (error.includes("auth/email-already-in-use")) {
            return (
                <p>
                    Este correo ya está registrado.{" "}
                    <Link to={"/login"} className="underline">
                        ¿Deseas iniciar sesión?
                    </Link>
                </p>
            );
        }
        if (error.includes("auth/google-account-already-registered")) {
            return (
                <p>
                    Esta cuenta de Google ya está registrada.{" "}
                    <Link to={"/login"} className="underline">
                        ¿Deseas iniciar sesión?
                    </Link>
                </p>
            );
        }
        if (error.includes("auth/account-exists-with-different-credential")) {
            return "Este correo ya existe con otro método de acceso. Inicia sesión con correo y contraseña para mantener tu cuenta actual.";
        }
        if (error.includes("auth/google-email-required")) {
            return "No pudimos obtener el correo de Google. Intenta con otra cuenta.";
        }
        if (
            error.includes("auth/popup-closed-by-user") ||
            error.includes("auth/cancelled-popup-request")
        ) {
            return "El acceso con Google fue cancelado.";
        }
        if (error.includes("auth/popup-blocked")) {
            return "El navegador bloqueó la ventana de Google. Permite popups para Maifud e intenta nuevamente.";
        }
        if (error.includes("auth/operation-not-allowed")) {
            return "El acceso con Google todavía no está habilitado en Firebase.";
        }
        return error;
    };

    const isCreatingUser =
        createUser.isPending || createUserWithGoogle.isPending;

    return (
        <Form {...form}>
            <form
                onSubmit={form.handleSubmit(handleSubmit)}
                className="space-y-4"
            >
                <FormField
                    control={form.control}
                    name="display_name"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Nombre</FormLabel>
                            <FormControl>
                                <Input
                                    placeholder="Ingresa tu nombre"
                                    autoComplete="name"
                                    onChange={(e) => {
                                        field.onChange(e);
                                        setRegistryData((r) => ({
                                            ...r,
                                            display_name: e.target.value,
                                        }));
                                    }}
                                    value={field.value}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="phone_number"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Teléfono</FormLabel>
                            <FormControl>
                                <PhoneInput
                                    id="phone_number"
                                    type="tel"
                                    placeholder="Ingresa tu número de teléfono"
                                    autoComplete="tel"
                                    value={field.value}
                                    defaultCountry="CL"
                                    className="font-sans"
                                    countries={["CL"]}
                                    onChange={(value) => {
                                        field.onChange(value);
                                        // console.log(value);
                                        setRegistryData((r) => ({
                                            ...r,
                                            phone_number: value,
                                        }));
                                    }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                {config.ENABLE_GOOGLE_AUTH && (
                    <div className="space-y-4 rounded-md border border-primary/30 bg-primary/5 p-4">
                        <div className="space-y-1 text-center">
                            <p className="text-sm font-semibold text-primary">
                                Recomendado
                            </p>
                            <p className="text-base font-semibold">
                                Crea tu cuenta con tu correo de Google
                            </p>
                        </div>
                        <Button
                            type="button"
                            className="w-full bg-primary text-white hover:bg-primary/90"
                            disabled={isCreatingUser}
                            isLoading={createUserWithGoogle.isPending}
                            onClick={handleGoogleSubmit}
                        >
                            <span className="font-semibold">G</span>
                            Crear cuenta con Google
                        </Button>
                    </div>
                )}
                <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-background px-2 text-muted-foreground">
                            o usa correo y contraseña
                        </span>
                    </div>
                </div>
                <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Email</FormLabel>
                            <FormControl>
                                <Input
                                    placeholder="Ingresa tu email"
                                    type="email"
                                    autoComplete="email"
                                    onChange={(e) => {
                                        field.onChange(e);
                                        setRegistryData((r) => ({
                                            ...r,
                                            email: e.target.value,
                                        }));
                                    }}
                                    value={field.value}
                                />
                            </FormControl>
                            <FormDescription>
                                🔔 Es importante que tu email esté bien escrito.
                                Toda la información de tu suscripción la
                                enviaremos por este medio.
                            </FormDescription>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Contraseña</FormLabel>
                            <FormControl>
                                <Input
                                    placeholder="Ingresa tu contraseña"
                                    type="password"
                                    autoComplete="new-password"
                                    id="new-password"
                                    onChange={field.onChange}
                                    value={field.value}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="confirm_password"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Confirmar contraseña</FormLabel>
                            <FormControl>
                                <Input
                                    placeholder="Ingresa tu contraseña"
                                    type="password"
                                    autoComplete="new-password"
                                    id="confirm_password"
                                    onChange={field.onChange}
                                    value={field.value}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <div>
                    <p className="text-center text-muted-foreground text-sm">
                        Al registrarte en Maifud, aceptas nuestros{" "}
                        <CustomLink
                            to="/terms-and-conditions"
                            className="underline text-primary"
                        >
                            Términos
                        </CustomLink>{" "}
                        y{" "}
                        <CustomLink
                            to="/privacy-policy"
                            className="underline text-primary"
                        >
                            Política de privacidad
                        </CustomLink>
                        .
                    </p>
                </div>
                <div className="flex justify-between pt-4 gap-4">
                    <Button
                        type="button"
                        variant="outline"
                        className="w-full sm:w-fit"
                        onClick={() =>
                            navigate({ to: "/register/shipping-frecuency" })
                        }
                    >
                        Volver
                    </Button>
                    <Button
                        type="submit"
                        className="w-full sm:w-fit"
                        disabled={isCreatingUser}
                        isLoading={createUser.isPending}
                    >
                        {createUser.isPending ? "Creando usuario" : "Siguiente"}
                    </Button>
                </div>
            </form>
            {(createUser.isError || createUserWithGoogle.isError) && (
                <FormMessage className="text-right">
                    {renderError(
                        createUser.error?.message ||
                            createUserWithGoogle.error?.message ||
                            "",
                    )}
                </FormMessage>
            )}
        </Form>
    );
};

export default UserDataForm;
